import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { surveyApi } from '../../services/api';
import { Spinner, EmptyState } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  LockClosedIcon, ShieldCheckIcon, CheckCircleIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

function RatingInput({ scale = 5, value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: scale }).map((_, i) => {
        const n = i + 1;
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n)}
            className={clsx(
              'w-11 h-11 rounded-lg border text-sm font-semibold transition-all',
              active
                ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:text-blue-600',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {n}
          </button>
        );
      })}
      <div className="flex items-center ml-1 text-xs text-gray-400 gap-4">
        <span>Low</span>
        <span className="flex-1"></span>
        <span>High</span>
      </div>
    </div>
  );
}

function YesNoInput({ value, onChange, disabled }) {
  const opts = [
    { v: 'yes', label: 'Yes', activeCls: 'bg-green-600 border-green-600 text-white' },
    { v: 'no',  label: 'No',  activeCls: 'bg-red-600 border-red-600 text-white' },
  ];
  return (
    <div className="flex gap-2">
      {opts.map(o => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.v)}
            className={clsx(
              'px-5 py-2 rounded-lg border text-sm font-medium transition-colors',
              active ? o.activeCls : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelectInput({ options = [], value = [], onChange, disabled }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div className="space-y-2">
      {options.map(opt => {
        const on = value.includes(opt);
        return (
          <label
            key={opt}
            className={clsx(
              'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
              on ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={on}
              disabled={disabled}
              onChange={() => toggle(opt)}
            />
            <span className="text-sm text-gray-800">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function SurveyRespond() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [answers, setAnswers] = useState({});

  const { data: survey, isLoading, error } = useQuery({
    queryKey: ['survey', id],
    queryFn:  () => surveyApi.get(id).then(r => r.data),
  });

  const respondMut = useMutation({
    mutationFn: (payload) => surveyApi.respond(id, payload),
    onSuccess:  () => {
      toast.success('Thanks — your response has been recorded');
      navigate('/app/surveys');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to submit response'),
  });

  const questions  = survey?.questions || [];
  const disabled   = survey?.status !== 'active';

  const answeredCount = useMemo(() => {
    return questions.filter(q => {
      const a = answers[q.id];
      if (a == null) return false;
      if (Array.isArray(a)) return a.length > 0;
      if (typeof a === 'string') return a.trim().length > 0;
      return true;
    }).length;
  }, [answers, questions]);

  const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  const setAnswer = (qid, val) => setAnswers(p => ({ ...p, [qid]: val }));

  const validate = () => {
    for (const q of questions) {
      if (!q.required) continue;
      const a = answers[q.id];
      if (a == null) return q;
      if (Array.isArray(a) && a.length === 0) return q;
      if (typeof a === 'string' && !a.trim()) return q;
    }
    return null;
  };

  const handleSubmit = () => {
    const missing = validate();
    if (missing) {
      toast.error(`Please answer: "${missing.label}"`);
      return;
    }
    const responses = questions.map(q => {
      const a = answers[q.id];
      const base = { question_id: q.id, answer: a ?? null };
      if (q.type === 'rating' && typeof a === 'number') {
        return { ...base, score: a };
      }
      if (q.type === 'yes_no') {
        return { ...base, score: a === 'yes' ? 1 : a === 'no' ? 0 : null };
      }
      return base;
    }).filter(r => r.answer != null && !(Array.isArray(r.answer) && r.answer.length === 0));
    respondMut.mutate({ responses });
  };

  if (isLoading) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  if (error || !survey) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Survey not found"
          description="This survey may have been removed or you don't have access to it."
          action={<Link to="/app/surveys" className="btn-primary">Back to surveys</Link>}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="max-w-3xl mx-auto">
        <Link to="/app/surveys" className="text-sm text-blue-600 hover:underline">← Surveys</Link>
        <h1 className="page-title mt-1">{survey.name}</h1>
        <p className="text-sm text-gray-500 mt-1 mb-5">
          {questions.length} question{questions.length === 1 ? '' : 's'}
          {survey.end_date && ` · Closes ${new Date(survey.end_date).toLocaleDateString()}`}
        </p>

        {survey.is_anonymous && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-start gap-3">
            <ShieldCheckIcon className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-800">
              This survey is anonymous — your identity is not recorded.
            </p>
          </div>
        )}

        {disabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                This survey is not currently active
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Status: <span className="capitalize font-medium">{survey.status}</span>. Responses
                cannot be submitted.
              </p>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur py-3 -mx-4 px-4 mb-4 border-b border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
            <span className="font-medium">Progress</span>
            <span>
              {answeredCount} / {questions.length} answered ({progress}%)
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={q.id} className="card p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <p className="text-xs text-gray-400 font-medium mb-0.5">
                    Question {i + 1} of {questions.length}
                  </p>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug">
                    {q.label}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </h3>
                </div>
                {answers[q.id] != null &&
                  !(Array.isArray(answers[q.id]) && answers[q.id].length === 0) &&
                  !(typeof answers[q.id] === 'string' && !answers[q.id].trim()) && (
                    <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />
                  )}
              </div>

              {q.type === 'rating' && (
                <RatingInput
                  scale={q.scale || 5}
                  value={answers[q.id]}
                  onChange={v => setAnswer(q.id, v)}
                  disabled={disabled}
                />
              )}
              {q.type === 'text' && (
                <textarea
                  className="input h-28 resize-none"
                  placeholder="Share your thoughts…"
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  disabled={disabled}
                />
              )}
              {q.type === 'yes_no' && (
                <YesNoInput
                  value={answers[q.id]}
                  onChange={v => setAnswer(q.id, v)}
                  disabled={disabled}
                />
              )}
              {q.type === 'multi_select' && (
                <MultiSelectInput
                  options={q.options || []}
                  value={answers[q.id] || []}
                  onChange={v => setAnswer(q.id, v)}
                  disabled={disabled}
                />
              )}
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur -mx-4 px-4 py-4 mt-6 border-t border-gray-100 flex items-center justify-between gap-3">
          <Link to="/app/surveys" className="btn-secondary">Cancel</Link>
          <button
            onClick={handleSubmit}
            disabled={disabled || respondMut.isPending}
            className="btn-primary disabled:opacity-40"
          >
            {disabled && <LockClosedIcon className="w-4 h-4" />}
            {respondMut.isPending ? 'Submitting…' : 'Submit response'}
          </button>
        </div>
      </div>
    </div>
  );
}
